import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { nextTick } from 'process';
import { CountryCode } from '../../../common/enums/country-code.enum';
import {
  extractDefaultBirthdateAndSexualForMY,
  isIdentityNoControlValid,
} from '../../../common/utils/malaysia-identityNo-parse.utils';
import { ValidatorsPlus } from '../../../common/validators/plus.validator';
import { isNullOrEmpty } from '../../../core/utils';
import { BaseComponent } from '../../../shared/components';
import { toUpperCase } from '../../public-api';

/**
 * 統編輸入元件
 * @description 用於畫面上輸入統編時使用
 * @description 目前規則：1.英文自動轉大寫
 */
@Component({
  selector: 'sigv-input-identity-no',
  templateUrl: './input-identity-no.component.html',
  styleUrls: ['./input-identity-no.component.scss'],
})
export class InputIdentityNoComponent
  extends BaseComponent
  implements OnInit, OnChanges
{
  /** 如有設定則會顯示於 formValidate時的錯誤訊息 */
  @Input() fieldName?: string;
  /** formControl物件
   * @description useForm.controls['externalName']
   */
  @Input() bindingFormControl?: AbstractControl;
  /** 空值欲設提示 */
  @Input() placeholder?: string;
  /** 欄位寬度 */
  @Input() maxlength?: number;
  /** 欄位寬度(單位) */
  @Input() maxlengthUnit?: string = 'px';
  /** 頁面是否純檢視*/
  @Input() isReadOnly: boolean = false;
  /** 是否啟用ID檢查 */
  @Input() isEnableIdCheck: boolean = false;
  /** 身份別 */
  @Input() identityFormControl?: AbstractControl;
  /** 出生日期FormContro; */
  @Input() birthdateFormControl?: AbstractControl;
  /** 性別FormControl */
  @Input() sexualFormControl?: AbstractControl;
  /** 舊Id */
  @Input() oldIdentityNo!: string;
  /** 是否即時連動性別及生日 (預設false) */
  @Input() isActivelyResetGenderAndBirthday: boolean = false;

  /** 因為會有 API 查資料回壓問題, 不是單純 Default Value, 加參數控制 */
  @Input() isIndentityNoOnBlurNeedSetDefaultField: boolean = false;

  @Input() fromDLW: boolean = false;
  /** style */
  @Input() style?: string = 'display: flex';
  /**blur事件 */
  @Output() blur: EventEmitter<any> = new EventEmitter();

  useWidth = '100%';
  countryId = globalThis.sigv.appContext.envConfig.locale;
  render = true;
  hasAddRules = false;
  tooltipMessage = '';
  constructor(private injector: Injector) {
    super(injector);
    this.useWidth = `${this.maxlength}${this.maxlengthUnit}`;
  }

  private util = {
    trasferToUpperCase: () => {
      this.bindingFormControl?.setValue(
        toUpperCase(this.bindingFormControl?.getRawValue() ?? '')
      );
    },
  };
  forceRender() {
    this.render = false;
    nextTick(() => {
      this.render = true;
    });
  }
  blurEvent(event: any) {
    this.util.trasferToUpperCase();

    if (this.isIndentityNoOnBlurNeedSetDefaultField) {
      this.trySetDefaultFieldsFromIdentityNo();
    }

    this.blur.emit(event);
  }

  /** 檢查身分證欄位是否存在且有效；若無控制項或有錯誤 → true */
  private hasIdentityNoErrors(): boolean {
    const ctrl = this.bindingFormControl;
    if (!ctrl) return true; // 沒有 control 就不做事
    // 先刷新一次驗證狀態，確保錯誤已掛上
    ctrl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    return ctrl.invalid === true;
  }

  ngOnInit() {
    this.logger.debug(
      'input-text init',
      ' bindingFormControl:',
      this.bindingFormControl
    );
    this.useWidth = `${String(this.maxlength)}${this.maxlengthUnit}`;
    this.applyIdChecker();
    this.subscribeToLangChange();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isEnableIdCheck']) {
      this.applyIdChecker();
    }
    if (changes['oldIdentityNo']) {
      if (!isNullOrEmpty(this.oldIdentityNo)) {
        this.useWidth = '80%';

        this.tooltipMessage = this.getTooltipText();
        this.forceRender();
      }
    }
  }

  getTooltipText(): string {
    return ` ${this.translateService.instant(
      'Library.ConsumerBaseInfo.oldIdNo'
    )} \n ${this.oldIdentityNo} `;
  }

  subscribeToLangChange() {
    this.translateService.onLangChange.subscribe((value) => {
      this.tooltipMessage = this.getTooltipText();
    });
  }

  applyIdChecker() {
    if (this.identityFormControl && this.isEnableIdCheck) {
      if (this.bindingFormControl?.hasValidator(ValidatorsPlus.idChecker)) {
        return;
      } else {
        if (!this.hasAddRules) {
          this.bindingFormControl?.addValidators(
            ValidatorsPlus.idChecker(
              this.identityFormControl,
              this.birthdateFormControl,
              this.sexualFormControl,
              this.fromDLW,
              this.isActivelyResetGenderAndBirthday
            ) as any
          );
          this.hasAddRules = true;
        }
      }
    } else {
      this.bindingFormControl?.removeValidators(ValidatorsPlus.idChecker);
      this.hasAddRules = false;
    }
    this.bindingFormControl?.updateValueAndValidity();
  }

  private trySetDefaultFieldsFromIdentityNo(): void {
    // 若此欄位驗證失敗則不做事
    if (this.hasIdentityNoErrors()) return;

    const identityNo = this.bindingFormControl?.getRawValue?.() ?? '';
    const country = (this.countryId || '').toString().toUpperCase();

    switch (country) {
      case CountryCode.MALAYSIA:
        this.applyDefaultsForMY(identityNo);
        break;
      default:
        return; // 其他國家先不處理
    }
  }

  private applyDefaultsForMY(identityNo: string): void {
    // Todo 這段先保留 idChecker 內處理
    if (this.isActivelyResetGenderAndBirthday) {
      return;
    }

    if (!isIdentityNoControlValid(this.bindingFormControl)) {
      return;
    }

    const defaults = extractDefaultBirthdateAndSexualForMY(identityNo);
    if (!defaults) return;

    // 個別沒有就補
    if (this.birthdateFormControl && defaults.birthdate) {
      const current = this.birthdateFormControl.getRawValue?.();
      if (isNullOrEmpty(current)) {
        this.birthdateFormControl.setValue(defaults.birthdate);
      }
    }

    if (this.sexualFormControl && defaults.gender) {
      const current = this.sexualFormControl.getRawValue?.();
      if (isNullOrEmpty(current)) {
        this.sexualFormControl.setValue(defaults.gender);
      }
    }
  }
}
